import { useQuery } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createIntentKey, listDestinations, useCreateDeliveryMutation } from '../../api/index.js';
import { Button } from '../../design-system/button.js';
import { Field, FieldLabel, Input } from '../../design-system/form.js';
import { Select } from '../../design-system/select.js';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '../../design-system/dialog.js';
import { Banner } from '../../design-system/surfaces.js';
import { SegmentedControl } from '../shared/page.js';
import { useWorkspace } from '../../app/runtime.js';

type DeliveryActionProps =
  | { trackId: string; generationId?: never; outputs?: never }
  | {
      generationId: string;
      outputs: readonly { index: number; title: string }[];
      trackId?: never;
    };

export function DeliveryAction({ generationId, outputs, trackId }: DeliveryActionProps) {
  const navigate = useNavigate();
  const { connection } = useWorkspace();
  const destinations = useQuery({
    queryKey: ['destinations'],
    queryFn: () => listDestinations(),
  });
  const active = destinations.data?.items.filter((item) => item.status === 'active') ?? [];
  const mutation = useCreateDeliveryMutation();
  const [destinationId, setDestinationId] = useState('');
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [selectionMode, setSelectionMode] = useState<'all' | 'indexes'>('all');
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);

  if (destinations.isPending || destinations.isError || active.length === 0) return null;
  const selected = destinationId || active[0]!.id;

  const isGeneration = generationId !== undefined;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const request = await mutation.mutateAsync({
      body: {
        destination_id: selected,
        source: isGeneration
          ? {
              type: 'generation',
              generation_id: generationId,
              output_selection:
                selectionMode === 'all'
                  ? { mode: 'all' }
                  : { mode: 'indexes', indexes: selectedIndexes },
            }
          : { type: 'track', track_id: trackId },
        asset_profile: 'archive-wav',
        metadata: {
          ...(title.trim() ? { title: title.trim() } : {}),
          ...(artist.trim() ? { artist: artist.trim() } : {}),
        },
      },
      idempotencyKey: createIntentKey(),
    });
    navigate(`/deliveries/${request.id}`);
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button disabled={connection.status !== 'active'} variant="secondary">
          <Send aria-hidden="true" /> Deliver
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="mx-auto w-full max-w-2xl">
        <div className="mx-auto mb-1 h-1 w-9 rounded-full bg-border-hi" aria-hidden="true" />
        <SheetTitle>{isGeneration ? 'Deliver generation' : 'Deliver track'}</SheetTitle>
        <SheetDescription>Send the original WAV to a configured destination.</SheetDescription>
        <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
          <Field>
            <FieldLabel>Destination</FieldLabel>
            <Select
              aria-label="Delivery destination"
              onValueChange={setDestinationId}
              options={active.map((destination) => ({
                value: destination.id,
                label: destination.name,
              }))}
              value={selected}
            />
          </Field>
          {isGeneration ? (
            <div className="grid gap-3">
              <SegmentedControl
                label="Outputs"
                value={selectionMode}
                onChange={setSelectionMode}
                options={[
                  { value: 'all', label: 'All outputs' },
                  { value: 'indexes', label: 'Choose outputs' },
                ]}
              />
              {selectionMode === 'indexes' ? (
                <fieldset className="grid gap-2">
                  <legend className="font-mono text-xs font-medium uppercase tracking-[0.1em] text-ink-3">
                    Select outputs
                  </legend>
                  {outputs.map((output) => {
                    const checked = selectedIndexes.includes(output.index);
                    return (
                      <label
                        key={output.index}
                        className="flex min-h-11 cursor-pointer items-center gap-3 rounded-sm border border-border bg-bg px-3 text-sm text-ink hover:border-border-hi"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setSelectedIndexes((current) =>
                              checked
                                ? current.filter((index) => index !== output.index)
                                : [...current, output.index].sort((a, b) => a - b),
                            )
                          }
                          className="size-4 accent-[var(--accent)]"
                        />
                        <span className="truncate">{output.title}</span>
                      </label>
                    );
                  })}
                </fieldset>
              ) : null}
            </div>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="delivery-title">Title (optional)</FieldLabel>
              <Input
                id="delivery-title"
                maxLength={512}
                onChange={(event) => setTitle(event.currentTarget.value)}
                value={title}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="delivery-artist">Artist (optional)</FieldLabel>
              <Input
                id="delivery-artist"
                maxLength={512}
                onChange={(event) => setArtist(event.currentTarget.value)}
                value={artist}
              />
            </Field>
          </div>
          {mutation.isError ? (
            <Banner title="Delivery could not be requested" tone="danger">
              No delivery was started. Review the destination and try again.
            </Banner>
          ) : null}
          <Button
            className="w-full"
            disabled={
              mutation.isPending ||
              (isGeneration && selectionMode === 'indexes' && selectedIndexes.length === 0)
            }
            type="submit"
          >
            {mutation.isPending ? 'Requesting delivery…' : 'Deliver'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
